/**
 * Diff visual side-by-side estilo Google Docs / GitHub.
 *
 * Usa `diff.diffLines` para alinear y muestra líneas eliminadas en rojo,
 * agregadas en verde. Texto sin cambios queda neutro.
 */
import { diffLines } from "diff";
import { useMemo } from "react";
import { cn } from "@/lib/cn";

interface DiffViewerProps {
  before: string;
  after: string;
  beforeLabel?: string;
  afterLabel?: string;
}

interface AlignedRow {
  beforeLine: string | null;
  afterLine: string | null;
  beforeMark: "del" | "ctx";
  afterMark: "add" | "ctx";
}

export function DiffViewer({
  before,
  after,
  beforeLabel = "Versión anterior",
  afterLabel = "Versión nueva",
}: DiffViewerProps) {
  const rows = useMemo<AlignedRow[]>(() => alignLines(before, after), [before, after]);

  const stats = useMemo(() => {
    let added = 0;
    let removed = 0;
    for (const r of rows) {
      if (r.afterMark === "add") added++;
      if (r.beforeMark === "del") removed++;
    }
    return { added, removed };
  }, [rows]);

  return (
    <div className="card-surface overflow-hidden">
      <div className="grid grid-cols-2 border-b border-subtle">
        <div className="px-4 py-2 border-r border-subtle bg-surface-2">
          <div className="font-sans text-xs font-semibold text-text-primary">
            {beforeLabel}
          </div>
          <div className="font-mono text-2xs text-text-muted">
            <span className="text-error-text">−{stats.removed}</span> líneas
            quitadas
          </div>
        </div>
        <div className="px-4 py-2 bg-surface-2">
          <div className="font-sans text-xs font-semibold text-text-primary">
            {afterLabel}
          </div>
          <div className="font-mono text-2xs text-text-muted">
            <span className="text-success-text">+{stats.added}</span> líneas
            agregadas
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 max-h-[600px] overflow-y-auto font-mono text-xs">
        {rows.map((row, i) => (
          <DiffLine key={i} row={row} />
        ))}
      </div>
    </div>
  );
}

function DiffLine({ row }: { row: AlignedRow }) {
  return (
    <>
      <div
        className={cn(
          "px-4 py-0.5 border-r border-subtle whitespace-pre-wrap break-words leading-relaxed",
          row.beforeMark === "del"
            ? "bg-error-bg text-error-text"
            : row.beforeLine === null
              ? "bg-surface-2"
              : "",
        )}
      >
        {row.beforeLine ?? " "}
      </div>
      <div
        className={cn(
          "px-4 py-0.5 whitespace-pre-wrap break-words leading-relaxed",
          row.afterMark === "add"
            ? "bg-success-bg text-success-text"
            : row.afterLine === null
              ? "bg-surface-2"
              : "",
        )}
      >
        {row.afterLine ?? " "}
      </div>
    </>
  );
}

/**
 * Genera filas side-by-side a partir de diffLines(before, after).
 * Estrategia: para cada hunk:
 *  - common → mismas líneas en ambas columnas, mark = ctx.
 *  - removed → líneas en left con mark=del, right vacío.
 *  - added → líneas en right con mark=add, left vacío.
 *  - removed seguido de added (modificación) → emparejar línea-por-línea
 *    para no desbalancear vertical.
 */
function alignLines(before: string, after: string): AlignedRow[] {
  const diff = diffLines(before, after);
  const result: AlignedRow[] = [];

  for (let i = 0; i < diff.length; i++) {
    const part = diff[i]!;
    const lines = part.value.replace(/\n$/, "").split("\n");

    if (!part.added && !part.removed) {
      for (const line of lines) {
        result.push({
          beforeLine: line,
          afterLine: line,
          beforeMark: "ctx",
          afterMark: "ctx",
        });
      }
      continue;
    }

    if (part.removed && diff[i + 1]?.added) {
      // Modificación: emparejar línea por línea.
      const next = diff[i + 1]!;
      const addedLines = next.value.replace(/\n$/, "").split("\n");
      const max = Math.max(lines.length, addedLines.length);
      for (let j = 0; j < max; j++) {
        result.push({
          beforeLine: lines[j] ?? null,
          afterLine: addedLines[j] ?? null,
          beforeMark: lines[j] != null ? "del" : "ctx",
          afterMark: addedLines[j] != null ? "add" : "ctx",
        });
      }
      i++; // saltar el "added" que ya consumimos
      continue;
    }

    if (part.removed) {
      for (const line of lines) {
        result.push({
          beforeLine: line,
          afterLine: null,
          beforeMark: "del",
          afterMark: "ctx",
        });
      }
      continue;
    }

    if (part.added) {
      for (const line of lines) {
        result.push({
          beforeLine: null,
          afterLine: line,
          beforeMark: "ctx",
          afterMark: "add",
        });
      }
    }
  }

  return result;
}
